import { FC, useMemo, useState, useEffect, useCallback } from "react";
import {
  Title,
  Divider,
  Button,
  Modal,
  Stack,
  Text,
  Group,
  Select,
  TextInput,
  Box,
} from "@mantine/core";
import { formatNumbersWithThousandSeperators } from "@funded-here-interface/common/src/Utils/formatter";
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import { UnclaimedMoneyInterface } from "../../../services/getUnclaimedMoney";
import { convertDateAndTime } from "@funded-here-interface/common/src/Utils/date";
import { toast } from "react-toastify";
import useGetUnclaimedMoney from "../../../hooks/useGetUnclaimedMoney";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";
import useGetVirtualAccounts from "../../../hooks/useGetVirtualAccounts";
import {
  Filter,
  VaAccountTab,
  VirtualAccount,
} from "../../../services/getVirtualAccounts";
import useInternalTransfer from "../../../hooks/useInternalTransfer";
import { useQueryClient } from "react-query";
import useUpdateUnclaimedMoney from "../../../hooks/useUpdateUnclaimedMoney";
import {
  FH_JUNIOR_INVESTOR_ACCOUNT_ID_DEV,
  FH_JUNIOR_INVESTOR_ACCOUNT_ID_PROD,
  FH_JUNIOR_INVESTOR_ACCOUNT_ID_UAT,
  vaAccountTypesOption,
} from "@funded-here-interface/common/src/constant/constant";
import { IconDownload } from "@tabler/icons-react";
import useDownloadUnclaimedMoneyCsv from "../../../hooks/useDownloadUnclaimedMoneyCsv";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import { FileType } from "@funded-here-interface/common/src/constant/enum";
import { AccountCurrencyTypeEnum } from "@funded-here-interface/common/src/constant/country";

// Constants
const vaTypes = vaAccountTypesOption;

const UnclaimedMoney: FC = () => {
  const { token, env } = useSelector((state: RootState) => state.auth);
  const queryClient = useQueryClient();

  //store pagination state in your own state
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [modalOpened, setModalOpened] = useState(false);
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<UnclaimedMoneyInterface | null>(null);

  // Claim form state
  const [selectedCurrency, setSelectedCurrency] = useState<string>("");
  const [receiverVAType, setReceiverVAType] = useState<string>("");
  const [receiverID, setReceiverID] = useState<string>("");
  const [receiverFilter, setReceiverFilter] = useState<Filter[]>([]);
  const [receiverVaAccountList, setReceiverVaAccountList] = useState<
    VirtualAccount[]
  >([]);
  const [receiverBalance, setReceiverBalance] = useState<string>("");
  const [receiverOrgDetails, setReceiverOrgDetails] = useState<string>("");
  const [receiverOrgName, setReceiverOrgName] = useState<string>("");
  const [receiverVirtualAccountId, setReceiverVirtualAccountId] =
    useState<string>("");
  const [remarks, setRemarks] = useState<string>("");

  const [formError, setFormError] = useState({
    currency: "",
    receiverVAType: "",
    receiverID: "",
    remarks: "",
  });

  // State - Junior investor account ID
  const [juniorInvestorAccountId, setJuniorInvestorAccountId] = useState("");
  const [isExportLoading, setIsExportLoading] = useState(false);

  const useInternalTransferMutation = useInternalTransfer();
  const useUpdateUnclaimedMoneyMutation = useUpdateUnclaimedMoney();
  const useDownloadUnknownMoneyCsvMutation = useDownloadUnclaimedMoneyCsv();

  const { data, error, isLoading } = useGetUnclaimedMoney(
    token,
    pagination,
    sorting,
    columnFilters
  );

  const { refetch: refetchReceiver } = useGetVirtualAccounts(
    {
      token,
      filter: receiverFilter.length > 0 ? receiverFilter : undefined,
      tab: VaAccountTab.INTERNAL_TRANSFER,
    },
    false
  );

  const totalRowCount = data?.totalRow ?? 0;

  if (error) {
    toast.error((error as Error).message);
  }

  // Helper functions
  const getFilter = useCallback(
    (vaType: string, currency: string) => {
      const filterInformation = [
        { id: "type", value: vaType },
        { id: "currency", value: currency },
      ];
      if (vaType === "investor") {
        filterInformation.push({
          id: "accountId",
          value: juniorInvestorAccountId,
        });
      }

      return filterInformation;
    },
    [juniorInvestorAccountId]
  );

  const fetchReceiver = useCallback(async () => {
    const result = await refetchReceiver();
    setReceiverVaAccountList(result.data?.vaAccounts ?? []);
  }, [refetchReceiver]);

  const clearClaimForm = () => {
    setSelectedCurrency("");
    setReceiverVAType("");
    setReceiverID("");
    setReceiverBalance("");
    setReceiverOrgDetails("");
    setReceiverOrgName("");
    setReceiverVirtualAccountId("");
    setRemarks("");
    setReceiverVaAccountList([]);
    setReceiverFilter([]);
    setFormError({
      currency: "",
      receiverVAType: "",
      receiverID: "",
      remarks: "",
    });
  };

  // Event handlers
  const handleClaim = (row: UnclaimedMoneyInterface) => {
    setSelectedRow(row);
    clearClaimForm();
    setModalOpened(true);
  };

  const handleModalClose = () => {
    setModalOpened(false);
    setSelectedRow(null);
    clearClaimForm();
  };

  const handleReceiverChange = (orgDetails: string) => {
    setFormError({ ...formError, receiverID: "" });
    const selectedOrg = receiverVaAccountList.find(
      (item) => item.orgDetails === orgDetails
    );
    if (selectedOrg) {
      setReceiverID(selectedOrg.id);
      setReceiverBalance(selectedOrg.balance);
      setReceiverVirtualAccountId(selectedOrg.accountId);
      setReceiverOrgName(
        selectedOrg.orgName ? selectedOrg.orgName : selectedOrg.refId
      );
      setReceiverOrgDetails(orgDetails);
    }
  };

  const handleReceiverVaTypeChange = async (vaType: string) => {
    setReceiverVaAccountList([]);
    setReceiverVAType(vaType);
    setReceiverID("");
    setReceiverOrgDetails("");
    setReceiverOrgName("");
    setReceiverBalance("");
    setReceiverVirtualAccountId("");
    if (!selectedCurrency) {
      return;
    }
    const filterInformation = getFilter(vaType, selectedCurrency);
    setReceiverFilter(filterInformation);
  };

  const handleCurrencyChange = (currency: string) => {
    setFormError({ ...formError, currency: "" });
    setSelectedCurrency(currency);
    setReceiverVaAccountList([]);
    setReceiverVAType("");
    setReceiverID("");
  };

  const handleConfirmClaim = () => {
    if (!selectedRow) return;

    const newErrors = {
      currency: "",
      receiverVAType: "",
      receiverID: "",
      remarks: "",
    };

    if (!selectedCurrency) {
      newErrors.currency = "Currency is required";
    }
    if (!receiverVAType) {
      newErrors.receiverVAType = "Receiver Virtual Account Type is required";
    }
    if (!receiverID) {
      newErrors.receiverID = "Receiver Virtual Account ID is required";
    }

    // Validate remarks: must contain at least one alphabetical character
    const trimmedRemarks = remarks.trim();
    if (!/[a-zA-Z]/.test(trimmedRemarks)) {
      newErrors.remarks = `Remark cannot be blank or "-". Please enter a valid remark.`;
    }

    setFormError(newErrors);

    const isValid = Object.values(newErrors).every((error) => error === "");

    if (isValid) {
      // Open confirmation modal
      setConfirmModalOpened(true);
    }
  };

  const handleExportData = () => {
    toast.info("Please wait a moment for system to export your data");
    setIsExportLoading(true);
    useDownloadUnknownMoneyCsvMutation.mutate(
      {
        token,
        sorting,
        columnFilters,
      },
      {
        onSuccess: (data: Blob) => {
          const exportDate = new Date();
          setIsExportLoading(false);
          downloadFile(
            data,
            `Unclaimed_Money_${exportDate.getFullYear()}_${
              exportDate.getMonth() + 1 //Month is zero-based
            }_${exportDate.getDate()}`,
            FileType.CSV
          );
        },
        onError: (e) => {
          setIsExportLoading(false);
          toast.error((e as Error).message);
        },
      }
    );
  };

  const executeTransfer = () => {
    if (!selectedRow || !selectedRow.receiverVaAccountId) return;

    useInternalTransferMutation.mutate(
      {
        token: token,
        senderVirtualAccountPrimaryId: selectedRow.receiverVaAccountId,
        receiverVirtualAccountPrimaryId: parseInt(receiverID, 10),
        currency: selectedCurrency,
        amount: selectedRow.amount,
        remarks,
      },
      {
        onSuccess: (data) => {
          useUpdateUnclaimedMoneyMutation.mutate(
            {
              token,
              paymentReceivableId: selectedRow.id,
              paymentRef: data.paymentRef,
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries("get-unclaimed-money");
                queryClient.invalidateQueries("get-vaacounts");
                toast.success("Payment claimed successfully");
                setConfirmModalOpened(false);
                handleModalClose();
              },
              onError: (error) => {
                toast.error((error as Error).message);
              },
            }
          );
        },
        onError: (error) => {
          toast.error((error as Error).message);
        },
      }
    );
  };

  // Effects
  useEffect(() => {
    if (receiverFilter.length > 0) {
      fetchReceiver();
    }
  }, [receiverFilter]);

  useEffect(() => {
    const junior_dev: string = FH_JUNIOR_INVESTOR_ACCOUNT_ID_DEV;
    const junior_uat: string = FH_JUNIOR_INVESTOR_ACCOUNT_ID_UAT;
    const junior_prod: string = FH_JUNIOR_INVESTOR_ACCOUNT_ID_PROD;

    let junior: string = "";

    if (env === "local") {
      junior = junior_dev;
    } else if (env === "uat") {
      junior = junior_uat;
    } else if (env === "production") {
      junior = junior_prod;
    }

    setJuniorInvestorAccountId(junior);
  }, [env]);

  const columns = useMemo<MRT_ColumnDef<UnclaimedMoneyInterface>[]>(
    () => [
      {
        accessorKey: "paymentReceivedDate",
        header: "Payment Received Date",
        filterVariant: "date-range",
        Cell: ({ cell }) => (
          <p>{convertDateAndTime(cell.getValue<string>(), true, 8)}</p>
        ),
      },
      {
        accessorKey: "createdDate",
        header: "Created Date",
        filterVariant: "date-range",
        Cell: ({ cell }) => (
          <p>{convertDateAndTime(cell.getValue<string>(), true, 8)}</p>
        ),
      },
      {
        accessorKey: "currency",
        header: "Currency",
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: Object.values(AccountCurrencyTypeEnum).map((v) => ({
            label: v,
            value: v,
          })),
          clearable: true,
        },
      },
      {
        accessorKey: "amount",
        header: "Amount",
        filterVariant: "range",
        mantineFilterTextInputProps: {
          type: "number",
          onKeyDown: (e) => {
            if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
          },
        },
        Cell: ({ cell }) => (
          <p>
            {formatNumbersWithThousandSeperators(
              parseFloat(cell.getValue<string>()),
              true
            )}
          </p>
        ),
      },
      {
        accessorKey: "senderAccountId",
        header: "Sender Account ID",
        accessorFn: (row) => row.senderAccountId ?? "",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "senderRefId",
        header: "Sender Reference ID",
        accessorFn: (row) => row.senderRefId ?? "",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "paymentRef",
        header: "PSP Payment Reference",
      },
      {
        accessorKey: "remarks",
        header: "Sender Payment Reference",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        id: "claim-receiverType",
        header: "Claim Receiver Virtual Account Type",
        accessorFn: (row) => row.claimedPayment?.receiverType ?? "",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            renderedCellValue === "moe" ? (
              <span>qc</span>
            ) : (
              <span>{renderedCellValue}</span>
            )
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        header: "Claim Receiver Virtual Account ID",
        accessorFn: (row) => row.claimedPayment?.receiverAccountId ?? "",
        id: "claim-receiverAccountId",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        header: "Claim Receiver Reference ID",
        accessorFn: (row) => row.claimedPayment?.receiverRefId ?? "",
        id: "claim-receiverRefId",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        header: "Claim Remark",
        accessorFn: (row) => row.claimedPayment?.remarks ?? "",
        id: "claim-remarks",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "isClaimed",
        header: "Status",
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: [
            { label: "Claimed", value: "true" },
            { label: "Unclaimed", value: "false" },
          ],
          clearable: true,
          placeholder: "Filter status",
        },
        Cell: ({ cell }) => (
          <p>{cell.getValue<boolean>() ? "Claimed" : "Unclaimed"}</p>
        ),
      },
      {
        header: "Claimed On",
        id: "claim-createdDate",
        accessorFn: (row) => row.claimedPayment?.createdDate ?? "",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          return value ? (
            <p>{convertDateAndTime(value, true, 8)}</p>
          ) : (
            <div>&#8210;</div>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Button
            size="xs"
            color="blue"
            onClick={() => handleClaim(row.original)}
            disabled={row.original?.isClaimed ? true : false}
          >
            {row.original.isClaimed ? "Claimed" : "Claim"}
          </Button>
        ),
      },
    ],
    []
  );

  const table = useMantineReactTable({
    enableColumnActions: false,
    enableColumnFilters: true,
    enableDensityToggle: false,
    enableGlobalFilter: false,
    enableSorting: true,
    state: {
      pagination,
      isLoading,
      sorting,
      columnFilters,
    },
    columns,
    data: data?.unclaimedMoney ?? [],
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    rowCount: totalRowCount,
    initialState: { density: "xs" },
    mantineTableProps: {
      withColumnBorders: true,
    },
    mantineTableHeadCellProps: {
      style: {
        padding: "0.5rem 1.0rem",
        color: "#23262a",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        padding: "0.5rem",
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <Button
          color="lightblue"
          onClick={handleExportData}
          leftIcon={<IconDownload />}
          variant="filled"
          loading={isExportLoading}
        >
          Export
        </Button>
      </Box>
    ),
  });

  return (
    <>
      <MantineReactTable table={table} />

      <Modal
        opened={modalOpened}
        onClose={handleModalClose}
        title={
          <span style={{ fontSize: "20px", fontWeight: 600 }}>
            Claim Payment
          </span>
        }
        size="xl"
      >
        {selectedRow && (
          <Stack spacing="md">
            <Divider />
            <Group position="apart">
              <Text weight={500}>Payment ID:</Text>
              <Text>{selectedRow.id}</Text>
            </Group>
            <Group position="apart">
              <Text weight={500}>PSP Payment Reference:</Text>
              <Text>{selectedRow.paymentRef}</Text>
            </Group>
            <Group position="apart">
              <Text weight={500}>Sender Payment Reference:</Text>
              <Text>{selectedRow.remarks}</Text>
            </Group>
            <Group position="apart">
              <Text weight={500}>Amount:</Text>
              <Text weight={700}>
                {selectedRow.currency}{" "}
                {formatNumbersWithThousandSeperators(
                  parseFloat(selectedRow.amount),
                  true
                )}
              </Text>
            </Group>
            <Divider />
            <Text weight={600} size="lg">
              Select Receiver
            </Text>

            <Select
              label="Currency"
              placeholder="Select currency"
              data={["SGD"]}
              value={selectedCurrency}
              onChange={(value) => handleCurrencyChange(value || "")}
              error={formError.currency}
              required
            />

            <Select
              label="Receiver Virtual Account Type"
              placeholder="Select receiver VA type"
              data={vaTypes.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              value={receiverVAType}
              onChange={(value) => {
                setFormError({ ...formError, receiverVAType: "" });
                const selectedType = vaTypes.find(
                  (option) => option.value === value
                );
                if (selectedType) {
                  handleReceiverVaTypeChange(selectedType.value);
                }
              }}
              error={formError.receiverVAType}
              disabled={!selectedCurrency}
              required
            />

            <Select
              label="Receiver Virtual Account ID"
              placeholder="Select receiver VA ID"
              data={receiverVaAccountList.map((item) => ({
                value: item.orgDetails || "",
                label: item.orgDetails || "",
              }))}
              value={receiverOrgDetails}
              onChange={(value) => handleReceiverChange(value || "")}
              error={formError.receiverID}
              disabled={receiverVaAccountList.length <= 0}
              required
              searchable
            />

            <TextInput
              label="Remarks"
              placeholder="Enter remarks"
              value={remarks}
              onChange={(event) => {
                setRemarks(event.currentTarget.value);
                setFormError({ ...formError, remarks: "" });
              }}
              error={formError.remarks}
              required
            />

            <Divider />
            <Group position="right" mt="md">
              <Button variant="outline" onClick={handleModalClose}>
                Cancel
              </Button>
              <Button color="blue" onClick={handleConfirmClaim}>
                Proceed
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        opened={confirmModalOpened}
        onClose={() => setConfirmModalOpened(false)}
        title={
          <Title order={4} style={{ fontWeight: "bold" }}>
            Confirm Claim Payment
          </Title>
        }
        size="lg"
      >
        {selectedRow && (
          <div style={{ padding: "0px 30px" }}>
            <p style={{ padding: "10px 0px" }}>
              Do you want to execute the following claim payment?
            </p>

            <p>
              <strong>Sender Virtual Account ID:</strong>{" "}
              {selectedRow.receiverAccountId || "—"}
            </p>
            <p>
              <strong>Sender:</strong>{" "}
              {selectedRow.receiverRefId || selectedRow.receiverType}
            </p>

            <p>
              <strong>Receiver Virtual Account ID:</strong>{" "}
              {receiverVirtualAccountId}
            </p>
            <p>
              <strong>Receiver:</strong> {receiverOrgName}
            </p>

            <p>
              <strong>Amount:</strong> {selectedCurrency}{" "}
              {formatNumbersWithThousandSeperators(
                parseFloat(selectedRow.amount),
                false,
                6
              )}
            </p>

            {receiverVirtualAccountId !== selectedRow.receiverAccountId && (
              <>
                <p>
                  <strong>Receiver Original Balance:</strong> {selectedCurrency}{" "}
                  {formatNumbersWithThousandSeperators(
                    parseFloat(receiverBalance),
                    false,
                    6
                  )}
                </p>
                <p>
                  <strong>Receiver Balance After Transfer:</strong>{" "}
                  {selectedCurrency}{" "}
                  {formatNumbersWithThousandSeperators(
                    parseFloat(receiverBalance) +
                      parseFloat(selectedRow.amount),
                    false,
                    6
                  )}
                </p>
              </>
            )}

            {receiverVirtualAccountId === selectedRow.receiverAccountId && (
              <>
                <p>
                  <strong>Sender Original Balance:</strong> {selectedCurrency}{" "}
                  {formatNumbersWithThousandSeperators(
                    parseFloat(selectedRow.receiverBalanceBefore || "0"),
                    false,
                    6
                  )}
                </p>
                <p>
                  <strong>Sender Balance After Transfer:</strong>{" "}
                  {selectedCurrency}{" "}
                  {formatNumbersWithThousandSeperators(
                    parseFloat(selectedRow.receiverBalanceBefore || "0") -
                      parseFloat(selectedRow.amount),
                    false,
                    6
                  )}
                </p>

                <p>
                  <strong>Receiver Original Balance:</strong> {selectedCurrency}{" "}
                  {formatNumbersWithThousandSeperators(
                    parseFloat(selectedRow.receiverBalanceBefore || "0") -
                      parseFloat(selectedRow.amount),
                    false,
                    6
                  )}
                </p>
                <p>
                  <strong>Receiver Balance After Transfer:</strong>{" "}
                  {selectedCurrency}{" "}
                  {formatNumbersWithThousandSeperators(
                    parseFloat(selectedRow.receiverBalanceBefore || "0"),
                    false,
                    6
                  )}
                </p>
              </>
            )}

            <p>
              <strong>Remarks: </strong>
              {remarks}
            </p>

            <Group position="center" mt="xl" spacing="md">
              <Button
                color="red"
                variant="outline"
                onClick={() => setConfirmModalOpened(false)}
              >
                Cancel
              </Button>
              <Button
                color="green"
                onClick={executeTransfer}
                loading={useInternalTransferMutation.isLoading}
              >
                Confirm
              </Button>
            </Group>
          </div>
        )}
      </Modal>
    </>
  );
};

export default UnclaimedMoney;
